import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Wrench, Camera, CircleDollarSign, CheckCircle, Upload, X, ArrowRight, ArrowLeft, Loader2, Crown, Tag, Car } from 'lucide-react';

const MAKES = ['Toyota', 'Honda', 'Nissan', 'Hyundai', 'Kia', 'Mercedes-Benz', 'BMW', 'Volkswagen', 'Ford', 'Chevrolet', 'Mazda', 'Subaru', 'Mitsubishi', 'Suzuki', 'Peugeot', 'Renault', 'Volvo', 'Audi', 'Porsche', 'Land Rover', 'Lexus', 'Universal/All Makes'];
const LOCATIONS = ['Accra', 'Kumasi', 'Tamale', 'Cape Coast', 'Takoradi', 'Tema', 'Ho', 'Sunyani', 'Bolgatanga', 'Koforidua'];
const PART_CATEGORIES = [
  "Engine & Drivetrain",
  "Body & Exterior",
  "Electrical & Lighting",
  "Suspension & Steering",
  "Brakes",
  "Interior & Comfort",
  "Wheels & Tires",
  "Transmission",
  "Cooling System",
  "Exhaust System",
  "Filters & Fluids",
  "AC & Heating",
  "Accessories"
];
const PART_CONDITIONS = ["New", "Used", "Refurbished"];

export default function CreatePartListing() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [form, setForm] = useState({
    title: '',
    part_category: '',
    compatible_makes: [],
    compatible_years: '',
    condition: 'Used',
    brand: '',
    part_number: '',
    price: '',
    description: '',
    location: 'Accra',
  });

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  // Check listing eligibility on mount
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const res = await API.get('/listings/eligibility');
        setEligibility(res.data);
      } catch (error) {
        console.error('Error checking eligibility:', error);
        setEligibility({ can_create_listing: true });
      } finally {
        setCheckingEligibility(false);
      }
    };
    checkEligibility();
  }, []);

  if (checkingEligibility) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </main>
    );
  }

  if (eligibility && !eligibility.can_create_listing) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-card border border-border/50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-8 h-8 text-gold" />
            </div>
            <h1 className="text-2xl font-bold mb-3">Listing Limit Reached</h1>
            <p className="text-muted-foreground mb-6">
              You've used all {eligibility.total_slots} of your listing slots.
            </p>
            <div className="space-y-4">
              <Button onClick={() => navigate('/buy-slots')} className="bg-gold text-black hover:brightness-110">
                Buy More Slots
              </Button>
              <Button onClick={() => navigate('/dealer')} variant="outline" className="w-full border-gold/50 text-gold">
                <Crown className="w-4 h-4 mr-2" /> Become a Dealer
              </Button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const toggleMake = (make) => {
    setForm(f => ({
      ...f,
      compatible_makes: f.compatible_makes.includes(make)
        ? f.compatible_makes.filter(m => m !== make)
        : [...f.compatible_makes, make]
    }));
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) { toast.error('Maximum 5 photos for parts'); return; }
    setUploading(true);
    
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      
      try {
        const res = await API.post('/upload/with-analysis', fd);
        setPhotos(p => [...p, { url: res.data.url, analysis: res.data.analysis }]);
      } catch (err) { 
        toast.error(`Failed to upload ${file.name}`); 
      }
    }
    setUploading(false);
  };

  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx));

  const submitListing = async () => {
    if (!form.title || !form.part_category || !form.price) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.compatible_makes.length === 0) {
      toast.error('Select at least one compatible make'); return;
    }
    
    // Photo requirements based on condition
    const minPhotos = form.condition === 'New' ? 1 : 2;
    if (photos.length < minPhotos) {
      toast.error(`${form.condition} parts require at least ${minPhotos} photo${minPhotos > 1 ? 's' : ''} to build trust`);
      return;
    }
    
    setLoading(true);
    try {
      const payload = { 
        listing_type: 'part',
        title: form.title,
        part_category: form.part_category,
        compatible_makes: form.compatible_makes,
        compatible_years: form.compatible_years || null,
        condition: form.condition,
        brand: form.brand || null,
        part_number: form.part_number || null,
        price: parseFloat(form.price),
        description: form.description,
        location: form.location,
        photos: photos.map(p => p.url)
      };
      const res = await API.post('/listings', payload);
      toast.success('Part listing created successfully!');
      navigate(`/listing/${res.data.listing_id}`);
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to create listing'); 
    }
    setLoading(false);
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <main data-testid="create-part-listing-page" className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex items-center gap-3 mb-2">
          <Wrench className="w-6 h-6 text-gold" />
          <h1 className="text-2xl font-bold">Sell Spare Parts</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">List your car spare parts for sale on AutoTrust Ghana.</p>
        
        {/* Step indicators */}
        <div className="flex gap-1 mb-10">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${s <= step ? 'bg-gold' : 'bg-muted'}`} />
          ))}
        </div>

        {/* Step 1: Part Details */}
        {step === 1 && (
          <div className="space-y-6" data-testid="part-step-1">
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
                  <Tag className="w-4 h-4 text-gold" />
                </div>
                <h2 className="font-semibold">Part Information</h2>
              </div>
              
              <div className="space-y-2">
                <Label>Part Name / Title *</Label>
                <Input 
                  placeholder="e.g. Toyota Camry 2018 Headlight Assembly" 
                  value={form.title} 
                  onChange={(e) => update('title', e.target.value)}
                  className="h-12" 
                  data-testid="part-title-input" 
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.part_category} onValueChange={(v) => update('part_category', v)}>
                  <SelectTrigger className="h-12" data-testid="part-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {PART_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Condition *</Label>
                  <Select value={form.condition} onValueChange={(v) => update('condition', v)}>
                    <SelectTrigger className="h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PART_CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Brand/Manufacturer</Label>
                  <Input 
                    placeholder="e.g. Bosch, OEM" 
                    value={form.brand} 
                    onChange={(e) => update('brand', e.target.value)}
                    className="h-12" 
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Part Number (Optional)</Label>
                <Input 
                  placeholder="e.g. 81110-06620" 
                  value={form.part_number} 
                  onChange={(e) => update('part_number', e.target.value)}
                  className="h-12 font-mono" 
                />
              </div>
            </div>
            
            {/* Compatible Vehicles */}
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
                  <Car className="w-4 h-4 text-gold" />
                </div>
                <h2 className="font-semibold">Compatible Vehicles</h2>
              </div>
              
              <div className="space-y-2">
                <Label>Compatible Makes * (select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {MAKES.map(make => (
                    <button
                      key={make}
                      type="button"
                      onClick={() => toggleMake(make)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        form.compatible_makes.includes(make)
                          ? 'bg-gold text-black'
                          : 'bg-muted hover:bg-muted/80 text-foreground'
                      }`}
                    >
                      {make}
                    </button>
                  ))}
                </div>
                {form.compatible_makes.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Selected: {form.compatible_makes.join(', ')}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Compatible Years (Optional)</Label>
                <Input 
                  placeholder="e.g. 2015-2020 or 2018+" 
                  value={form.compatible_years} 
                  onChange={(e) => update('compatible_years', e.target.value)}
                  className="h-12" 
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} className="bg-gold text-black hover:brightness-110 gap-2 h-12 px-8" data-testid="part-next-step-btn">
                Next: Photos <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Photos */}
        {step === 2 && (
          <div className="space-y-6" data-testid="part-step-2">
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
                  <Camera className="w-4 h-4 text-gold" />
                </div>
                <h2 className="font-semibold">Photos & Description</h2>
              </div>
              
              {/* Photo requirement notice */}
              <div className={`text-sm p-3 rounded-lg ${
                form.condition === 'New' 
                  ? 'bg-trust-green/10 border border-trust-green/30 text-trust-green'
                  : 'bg-gold/10 border border-gold/30 text-gold'
              }`}>
                {form.condition === 'New' 
                  ? '✓ New parts require at least 1 photo'
                  : `⚠️ ${form.condition} parts require at least 2 photos to build buyer trust`
                }
              </div>
              
              <div>
                <label className={`flex flex-col items-center gap-3 border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-gold/50 transition-colors ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
                  {uploading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-gold animate-spin" />
                      <span className="text-sm text-muted-foreground">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Click to upload photos (max 5)</span>
                      <span className="text-xs text-muted-foreground">
                        {photos.length} / {form.condition === 'New' ? '1' : '2'} minimum uploaded
                      </span>
                    </>
                  )}
                  <input type="file" accept="image/*" multiple onChange={uploadPhoto} className="hidden" disabled={uploading} />
                </label>
                {photos.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-4">
                    {photos.map((p, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border/50 group">
                        <img src={`${BACKEND_URL}${p.url}`} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  placeholder="Describe the part - condition, any defects, why you're selling..." 
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)} 
                  rows={4} 
                  data-testid="part-description-input" 
                />
              </div>
            </div>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} className="gap-2 h-12">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              <Button onClick={() => setStep(3)} className="bg-gold text-black hover:brightness-110 gap-2 h-12 px-8">
                Next: Pricing <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing & Location */}
        {step === 3 && (
          <div className="space-y-6" data-testid="part-step-3">
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
                  <CircleDollarSign className="w-4 h-4 text-gold" />
                </div>
                <h2 className="font-semibold">Pricing & Location</h2>
              </div>
              
              <div className="space-y-2">
                <Label>Price (GHS) *</Label>
                <Input 
                  type="number" 
                  placeholder="e.g. 500" 
                  value={form.price} 
                  onChange={(e) => update('price', e.target.value)}
                  className="font-mono-display h-12 text-lg" 
                  data-testid="part-price-input" 
                />
              </div>
              
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={form.location} onValueChange={(v) => update('location', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Summary */}
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-4">
              <h3 className="font-semibold text-gold mb-2">Listing Summary</h3>
              <div className="text-sm space-y-1 text-muted-foreground">
                <p><span className="text-foreground">{form.title || 'Untitled'}</span></p>
                <p>Category: {form.part_category || 'Not selected'}</p>
                <p>Condition: {form.condition}</p>
                <p>Compatible: {form.compatible_makes.length > 0 ? form.compatible_makes.join(', ') : 'Not selected'}</p>
                <p>Price: GHS {form.price || '0'}</p>
                <p>Photos: {photos.length}</p>
              </div>
            </div>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} className="gap-2 h-12">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              <Button 
                onClick={submitListing} 
                disabled={loading} 
                className="bg-gold text-black hover:brightness-110 gap-2 h-12 px-8" 
                data-testid="submit-part-listing-btn"
              >
                {loading ? 'Creating...' : 'Publish Part Listing'} <CheckCircle className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
